import { VC_AGENT_SESSION_BOM_ITEMS_TAG } from 'store/tags/valueChainSession';

import type IAgentSessionBomLineItem from 'store/slices/valueChainMap/interfaces/IAgentSessionBomLineItem';
import type IGetAgentSessionBomItemsRequest from 'store/slices/valueChainMap/interfaces/IGetAgentSessionBomItemsRequest';
import type { IBaseQueryPaginationResponse } from 'store/types/IBaseQueryPaginationResponse';
import type { TApi } from 'store/types/TApi';
import type { AppDispatch, RootState } from 'store/types/TStore';

type TBomItemsApi = TApi<IGetAgentSessionBomItemsRequest, IAgentSessionBomLineItem[], 'getAgentSessionBomItems'>;

type TBomItemsCache = IBaseQueryPaginationResponse<IAgentSessionBomLineItem[]>;

const recalculateMeta = (cache: TBomItemsCache) => {
  const { meta, items } = cache.data;

  if (!meta) {
    return cache;
  }

  return {
    ...cache,
    data: {
      ...cache.data,
      meta: {
        ...meta,
        hasNext: meta.count > meta.offset + items.length,
      },
    },
  };
};

const updateBomItemsQueries = ({
  valueChainMapApi,
  state,
  dispatch,
  valueChainMapId,
  sessionId,
  bomId,
  updater,
}: {
  valueChainMapApi: TBomItemsApi,
  state: RootState,
  dispatch: AppDispatch,
  valueChainMapId: string,
  sessionId: number,
  bomId: number,
  updater: (cache: TBomItemsCache, originalArgs: IGetAgentSessionBomItemsRequest) => TBomItemsCache,
}) => {
  valueChainMapApi.util.selectInvalidatedBy(state, [{
    type: VC_AGENT_SESSION_BOM_ITEMS_TAG,
    id: `${valueChainMapId}-${sessionId}-${bomId}`,
  }]).forEach(({ endpointName, originalArgs }) => {
    if (endpointName !== 'getAgentSessionBomItems') {
      return;
    }

    dispatch(valueChainMapApi.util.updateQueryData(
      endpointName,
      originalArgs as IGetAgentSessionBomItemsRequest,
      (cache) => {
        const updatedCache = updater(
          cache as TBomItemsCache,
          originalArgs as IGetAgentSessionBomItemsRequest,
        );

        return recalculateMeta(updatedCache);
      },
    ));
  });
};

export const syncCreatedBomItemInCaches = ({
  valueChainMapApi,
  state,
  dispatch,
  valueChainMapId,
  sessionId,
  bomId,
  item,
}: {
  valueChainMapApi: TBomItemsApi,
  state: RootState,
  dispatch: AppDispatch,
  valueChainMapId: string,
  sessionId: number,
  bomId: number,
  item: IAgentSessionBomLineItem,
}) => {
  updateBomItemsQueries({
    valueChainMapApi,
    state,
    dispatch,
    valueChainMapId,
    sessionId,
    bomId,
    updater: (cache, originalArgs) => {
      const nextMeta = {
        ...cache.data.meta,
        count: cache.data.meta.count + 1,
      };

      if ((originalArgs.offset || 0) !== 0) {
        return {
          ...cache,
          data: {
            ...cache.data,
            meta: nextMeta,
          },
        };
      }

      const nextItems = [item, ...cache.data.items.filter((cachedItem) => cachedItem.id !== item.id)];
      const limit = originalArgs.limit ?? cache.data.meta.limit;
      return {
        ...cache,
        data: {
          ...cache.data,
          items: nextItems.slice(0, limit),
          meta: nextMeta,
        },
      };
    },
  });
};

export const syncUpdatedBomItemInCaches = ({
  valueChainMapApi,
  state,
  dispatch,
  valueChainMapId,
  sessionId,
  bomId,
  item,
}: {
  valueChainMapApi: TBomItemsApi,
  state: RootState,
  dispatch: AppDispatch,
  valueChainMapId: string,
  sessionId: number,
  bomId: number,
  item: IAgentSessionBomLineItem,
}) => {
  updateBomItemsQueries({
    valueChainMapApi,
    state,
    dispatch,
    valueChainMapId,
    sessionId,
    bomId,
    updater: (cache) => {
      const itemIndex = cache.data.items.findIndex((cachedItem) => cachedItem.id === item.id);

      if (itemIndex === -1) {
        return cache;
      }

      return {
        ...cache,
        data: {
          ...cache.data,
          items: cache.data.items.map((cachedItem) => (
            cachedItem.id === item.id ? item : cachedItem
          )),
        },
      };
    },
  });
};

export const syncDeletedBomItemInCaches = ({
  valueChainMapApi,
  state,
  dispatch,
  valueChainMapId,
  sessionId,
  bomId,
  itemId,
}: {
  valueChainMapApi: TBomItemsApi,
  state: RootState,
  dispatch: AppDispatch,
  valueChainMapId: string,
  sessionId: number,
  bomId: number,
  itemId: number,
}) => {
  updateBomItemsQueries({
    valueChainMapApi,
    state,
    dispatch,
    valueChainMapId,
    sessionId,
    bomId,
    updater: (cache) => {
      const nextItems = cache.data.items.filter((cachedItem) => cachedItem.id !== itemId);

      if (nextItems.length === cache.data.items.length) {
        return cache;
      }

      return {
        ...cache,
        data: {
          ...cache.data,
          items: nextItems,
          meta: {
            ...cache.data.meta,
            count: Math.max(cache.data.meta.count - 1, 0),
          },
        },
      };
    },
  });
};

export const syncBomSelectionInCaches = ({
  valueChainMapApi,
  state,
  dispatch,
  valueChainMapId,
  sessionId,
  bomId,
  isActive,
}: {
  valueChainMapApi: TBomItemsApi,
  state: RootState,
  dispatch: AppDispatch,
  valueChainMapId: string,
  sessionId: number,
  bomId: number,
  isActive: boolean,
}) => {
  updateBomItemsQueries({
    valueChainMapApi,
    state,
    dispatch,
    valueChainMapId,
    sessionId,
    bomId,
    updater: (cache) => ({
      ...cache,
      data: {
        ...cache.data,
        items: cache.data.items.map((item) => ({ ...item, isActive })),
      },
    }),
  });
};
